import { useRef, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "../../../store";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import cn from "classnames";
import { createHubSpotContact } from "../../../api/hubSpot";
import {
  getContactRedirect,
  getPricingPackage,
} from "../../../api/fieldRoutes";
import { toast } from "react-toastify";
import postalCodesData from "../../../assets/json/postal_codes.json";
//Components
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import ReactInputMask from "react-input-mask";
import ContactUsToast from "../../../components/Toast/ContactUsToast/ContactUsToast";
import FullScreenLoader from "../../../components/FullScreenLoader/FullScreenLoader";
//Styles
import styles from "./ContactForm.module.scss";
//Amplitude
import * as amplitude from "@amplitude/analytics-browser";
import { extractAddressComponents } from "../../../helpers/address";
import { isDevelopment } from "../../../config/env";

const generalErrorEvent = {
  event_type: "Error",
  display_name: "Error",
  event_properties: {
    page_name: "eComm - Personal information",
    error_name: "Something went wrong",
  },
};

const validationSchema = yup.object({
  firstName: yup
    .string()
    .required("First Name is required")
    .matches(
      /^[A-z'-\s]+$/,
      "First Name must not contain digits or unauthorized symbols"
    ),
  lastName: yup
    .string()
    .required("Last Name is required")
    .matches(
      /^[A-z'-\s]+$/,
      "Last Name must not contain digits or unauthorized symbols"
    ),
  address: yup.string().required("Address is required"),
  unitNumber: yup.string().notRequired(),
  email: yup
    .string()
    .required("Email is required")
    .matches(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      "Email is invalid"
    ),
  mobilePhone: yup
    .string()
    .required("Mobile Phone is required")
    .test(
      "len",
      "Mobile Phone must be exactly 10 digits",
      (value) => value.replace(/_/g, "").length === 14
    ),
});

const ContactForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isFormDisabled, setIsFormDisabled] = useState(true);
  const [isAddressDropdownOpen, setIsAddressDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // custom error to check if Google Place has valid ZIP code
  const [emailError, setEmailError] = useState("");
  const [addressZipError, setAddressZipError] = useState("");
  const {
    contactInfo,
    setContactInfo,
    addressObject,
    setAddressObject,
    chosenPackage,
    setChosenPackage,
  } = useStore();

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: contactInfo,
    mode: "onBlur",
  });

  const handleSubmitForm = async (data) => {
    const errorEvent = {
      event_type: "Error",
      display_name: "Error",
      event_properties: {
        page_name: "eComm - Personal information",
        error_name: "Failed to create Contact in Hubspot",
        error_source: "Hubspot",
      },
    };

    const buttonClickedEvent = {
      event_type: "Button Clicked",
      display_name: "Button Clicked",
      event_properties: {
        page_name: "eComm - Personal information",
        button_name: "Next",
      },
    };

    amplitude.track(buttonClickedEvent);

    if (!addressObject) {
      setAddressZipError("Address is required");
      return;
    }

    const postalCodeObject = addressObject?.address_components?.find(
      (addressComponent) => addressComponent?.types?.includes("postal_code")
    );

    if (addressObject && !postalCodeObject) {
      setAddressZipError("Address must include valid ZIP code");
      return;
    }

    setContactInfo(data);

    const validPostalCode = postalCodesData?.find(
      (postalCode) => postalCode.Zip?.toString() === postalCodeObject?.long_name
    );

    const formattedAddress = extractAddressComponents(addressObject);
    const officeId = postalCodesData.find(
      (item) => `${item["Zip"]}` === formattedAddress.postal_code
    )?.["Office ID"];

    if (!isDevelopment && !officeId) {
      const pageViewedEvent = {
        event_type: "Page Viewed",
        display_name: "Page Viewed",
        event_properties: {
          page_name: "Wix - Out of coverage (from eComm)",
        },
      };

      amplitude.track(pageViewedEvent);

      window.location.replace("https://www.magnapest.com/out-of-service-area");
    }

    setIsSubmitting(true);

    await getContactRedirect({
      email: data?.email,
      address: formattedAddress.line1.trim(),
      city: formattedAddress.city,
      state: formattedAddress.state,
      zip: formattedAddress.postal_code,
      officeID: isDevelopment ? 19 : officeId,
    })
      .catch(() => {
        amplitude.track(generalErrorEvent);

        toast(<ContactUsToast />, { theme: "dark" });
        return;
      })
      .then((response) => {
        if (response?.data === "contact") {
          const pageViewedEvent = {
            event_type: "Page Viewed",
            display_name: "Page Viewed",
            event_properties: {
              page_name: "Wix - Contact (from eComm)",
            },
          };

          amplitude.track(pageViewedEvent);

          window.location.replace("https://www.magnapest.com/contact");
        }
        if (response?.data === "add-ons") {
          const pageViewedEvent = {
            event_type: "Page Viewed",
            display_name: "Page Viewed",
            event_properties: {
              page_name: "Wix - Already a PPP member (from eComm)",
            },
          };

          amplitude.track(pageViewedEvent);

          window.location.replace(
            "https://www.magnapest.com/service-overview/add-ons"
          );
        }
        if (response?.data === "proceed") {
          //ignore and continue flow (the customer doesn't exist)
        }
      });

    try {
      await createHubSpotContact({
        properties: {
          email: data?.email,
          firstname: data?.firstName,
          lastname: data?.lastName,
          phone: data?.mobilePhone,
          lifecyclestage: "lead",
          lead_source: "ecommerce",
          address:
            data?.unitNumber === ""
              ? addressObject.formatted_address
              : `${addressObject.formatted_address} ${data?.unitNumber}`,
          hs_lead_status: "OPEN",
          postal_code: formattedAddress.postal_code,
          utm_creative: searchParams.get("utm_creative"),
          utm_source: searchParams.get("utm_source"),
          utm_medium: searchParams.get("utm_medium"),
          utm_campaign: searchParams.get("utm_campaign"),
          ad_name: searchParams.get("hsa_ad"),
          campaign_name: searchParams.get("hsa_cam"),
          utm_agency: searchParams.get("utm_agency"),
          utm_term: searchParams.get("utm_term"),
          utm_content: searchParams.get("utm_content"),
        },
      });

      const leadCreationEvent = {
        event_type: "Lead Creation",
        display_name: "Lead Creation",
        event_properties: {
          page_name: "eComm - Personal information",
          has_quote: !!chosenPackage,
          user_name: `${data?.firstName} ${data?.lastName}`,
          user_email: data?.email,
          user_phone: data?.mobilePhone,
          user_address: addressObject.formatted_address,
        },
      };

      amplitude.track(leadCreationEvent);
    } catch (error) {
      if (error?.response?.data?.category === "VALIDATION_ERROR") {
        amplitude.track(errorEvent);

        setEmailError("Email is invalid");
      }
      if (error?.response?.data?.category === "CONFLICT") {
        // ignore error if contact already exists
      } else {
        amplitude.track(errorEvent);

        toast(<ContactUsToast />, { theme: "dark" });
        setIsSubmitting(false);
        return;
      }
    }

    setIsSubmitting(false);

    if (!validPostalCode) {
      const pageViewedEvent = {
        event_type: "Page Viewed",
        display_name: "Page Viewed",
        event_properties: {
          page_name: "Wix - Out of coverage (from eComm)",
        },
      };

      amplitude.track(pageViewedEvent);

      window.location.replace("https://www.magnapest.com/out-of-service-area");
    }
    if (chosenPackage === null && validPostalCode) {
      const pageViewedEvent = {
        event_type: "Page Viewed",
        display_name: "Page Viewed",
        event_properties: {
          page_name: "Wix - No quote (from eComm)",
        },
      };

      amplitude.track(pageViewedEvent);

      window.location.replace("https://magnapest.com/contact-us");
    }
    if (validPostalCode && chosenPackage) {
      navigate("/schedule");
    }
  };

  const handleBackClick = () => {
    const buttonClickedEvent = {
      event_type: "Button Clicked",
      display_name: "Button Clicked",
      event_properties: {
        page_name: "eComm - Personal information",
        button_name: "Back",
      },
    };

    amplitude.track(buttonClickedEvent);

    window.location.replace("https://www.magnapest.com/pricing");
  };

  const autoCompleteRef = useRef();
  const addressInputRef = useRef();

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const response = await getPricingPackage();
        setChosenPackage(response?.data?.serviceTypes[0]);
      } catch (err) {
        amplitude.track(generalErrorEvent);
        toast(<ContactUsToast />, { theme: "dark" });
      }
    };

    fetchPackage();
  }, [setChosenPackage]);

  // set address value from contactInfo on initial render
  useEffect(() => {
    if (contactInfo) {
      addressInputRef.current.value = contactInfo.address;
      setValue("address", contactInfo.address);
    }
  }, [contactInfo, setValue]);

  useEffect(() => {
    autoCompleteRef.current = new window.google.maps.places.Autocomplete(
      addressInputRef.current,
      {
        componentRestrictions: { country: "us" },
        fields: [
          "place_id",
          "address_components",
          "formatted_address",
          "name",
          "geometry",
        ],
        types: ["address"],
        language: "en",
      }
    );

    autoCompleteRef.current.addListener("place_changed", async function () {
      const place = await autoCompleteRef.current.getPlace();
      if (place) {
        addressInputRef.current.value = place.formatted_address;
        setAddressObject(place);
        setValue("address", place.formatted_address);
        clearErrors("address");
        setAddressZipError("");
      }
    });
  }, [setValue, clearErrors, setAddressObject]);

  // use to apply styles to input
  useEffect(() => {
    let observer;

    // register observer with timeout to let google load autocomplete to the DOM
    setTimeout(() => {
      let elements = document.querySelectorAll(".pac-container");

      if (elements.length) {
        elements.forEach(function (element) {
          observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
              if (
                mutation.type === "attributes" &&
                mutation.attributeName === "style"
              ) {
                if (mutation.oldValue.includes("display: none")) {
                  setIsAddressDropdownOpen(true);
                } else {
                  setIsAddressDropdownOpen(false);
                }
              }
            });
          });

          observer.observe(element, {
            attributes: true,
            attributeFilter: ["style"],
            attributeOldValue: true,
          });
        });
      }
    }, 2000);

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (contactInfo.address && !isDirty) {
      setIsFormDisabled(false);
    } else {
      setIsFormDisabled(!isDirty || !isValid);
    }
  }, [contactInfo, isDirty, isValid]);

  const handleOnAddressBlur = () => {
    if (
      (addressObject &&
        addressObject.formatted_address &&
        addressInputRef.current.value !== addressObject.formatted_address) ||
      (!addressObject && !addressInputRef.current.value)
    ) {
      setAddressObject(null);
      setAddressZipError("Address is required");
    }

    if (!addressObject && addressInputRef.current.value) {
      setAddressZipError("Select a verified address from the list");
    }
  };

  useEffect(() => {
    const pageViewedEvent = {
      event_type: "Page Viewed",
      display_name: "Page Viewed",
      event_properties: {
        page_name: "eComm - Personal information",
      },
    };

    amplitude.track(pageViewedEvent);
  }, []);

  return (
    <div className={styles.root}>
      {isSubmitting && <FullScreenLoader />}

      <div className={styles.formInputContainer}>
        <Input
          {...register("firstName")}
          error={errors.firstName?.message}
          className={styles.formInput}
          label="First Name"
          required={true}
        />
        <Input
          {...register("lastName")}
          error={errors.lastName?.message}
          className={styles.formInput}
          label="Last Name"
          required={true}
        />
      </div>
      <div className={styles.addressInputContainer}>
        <Input
          className={cn(styles.addressInputWidth, {
            [styles.addressInput]: isAddressDropdownOpen,
          })}
          label="Address"
          placeholder="e.g. 200 Gerard St, Austin, TX 78701"
          required={true}
          autoComplete="off"
          ref={addressInputRef}
          error={errors.address?.message || addressZipError}
          onBlur={handleOnAddressBlur}
        />
        <Input
          className={styles.unitNumberInput}
          label="Unit/Apt #"
          placeholder="1A"
          {...register("unitNumber")}
        />
      </div>
      <div className={styles.formInputContainer}>
        <Input
          {...register("email", {
            onChange: () => {
              clearErrors("email");
              setEmailError("");
            },
          })}
          error={errors.email?.message || emailError}
          className={styles.formInput}
          label="Email"
          required={true}
        />
        <ReactInputMask {...register("mobilePhone")} mask="(999) 999-9999">
          {(inputProps) => (
            <Input
              {...inputProps}
              error={errors.mobilePhone?.message}
              className={styles.formInput}
              label="Mobile Phone"
              placeholder="(000) 000-0000"
              required={true}
              inputmode="numeric"
            />
          )}
        </ReactInputMask>
      </div>
      <div className={styles.buttonsContainer}>
        <Button
          disabled={isFormDisabled}
          block
          onClick={handleSubmit(handleSubmitForm)}
        >
          Next
        </Button>
        <Button block type="outline" onClick={handleBackClick}>
          Back
        </Button>
      </div>
    </div>
  );
};

export default ContactForm;
